import { Component, Vue } from 'vue-property-decorator';
import { sortBy, startCase, uniqBy } from 'lodash';
import { PRIZE_TYPES } from '@/constants';
import { PrizeDTO, PrizeRequestDTO, SelectionOptions, TableHeaderColumns } from '@/models';
import { createOrUpdatePrize, fetchPrizes } from '@/services';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import PrizeForm from '@/components/pages/reward-management/prize-management/prize-form/PrizeForm.vue';
import blankImage from '@/assets/images/blankImage.png';

@Component({
  components: {
    FilterDropdown,
    InlineLoader,
    Pagination,
    PrizeForm,
    SearchInput
  }
})
export default class PrizeManagement extends Vue {
  private imageUrl = process.env.VUE_APP_HIVE_HOME_URL + '/images/';
  private prizes: PrizeDTO[] = [];
  private prize: PrizeDTO = this.buildDefaultPrize();
  private selectedPrizeTypes = [] as string[];
  private selectedPrize = {};
  private searchName = '';
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;
  private isFetching = true;
  private prizeFormShown = false;
  private blankImage = blankImage;

  get tableHeaderColumns(): TableHeaderColumns {
    return [
      { text: 'Name', value: 'name', class: 'grey--text', align: 'start', width: '15%' },
      { text: 'Image', value: 'image', class: 'grey--text', align: 'start', width: '20%', sortable: false },
      { text: 'Description', value: 'description', class: 'grey--text', align: 'start', width: '25%' },
      {
        text: 'Type',
        value: 'type',
        class: 'grey--text',
        align: 'start',
        width: '10%',
        filter: (value: string) => {
          return this.selectedPrizeTypes.length === 0 || this.selectedPrizeTypes.includes(value);
        }
      },
      { text: 'Actions', value: 'actions', align: 'center', width: '5%', sortable: false }
    ];
  }

  get prizeTypeOptions(): SelectionOptions {
    const options = [] as SelectionOptions;
    uniqBy(this.prizes, 'type').forEach(item => {
      if (item) {
        options.push({ text: item.type, value: item.type });
      }
    });
    return sortBy(options, 'text');
  }

  getImageSource(imgStr: string): string {
    return this.imageUrl + imgStr;
  }

  filterByPrizeName<T>(value: keyof T, search: string, item: PrizeDTO): boolean {
    return value !== null && search !== null && item.name.toLowerCase().includes(search.toLowerCase());
  }

  formatDisplayType(type: string): string {
    return startCase(type.toLowerCase());
  }

  onPrizeTypeChange(selectedPrizeTypes: string[]): void {
    this.selectedPrizeTypes = selectedPrizeTypes;
  }

  onSearch(searchText: string): void {
    this.searchName = searchText;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  buildDefaultPrize(): PrizeDTO {
    return {
      name: '',
      type: PRIZE_TYPES.BACKPACK,
      image: '',
      description: ''
    };
  }

  openCreateDialog(): void {
    this.prize = this.buildDefaultPrize();
    this.prizeFormShown = true;
  }

  openEditDialog(prizeToEdit: PrizeDTO): void {
    this.prize = { ...prizeToEdit, image: prizeToEdit.image ? this.getImageSource(prizeToEdit.image) : '' };
    this.prizeFormShown = true;
  }

  async onSave(prize: PrizeRequestDTO): Promise<void> {
    this.isFetching = true;
    const { data } = await createOrUpdatePrize(prize);
    this.isFetching = false;
    if (prize.id) {
      this.prizes = this.prizes.map(p => (p.id === data.id ? data : p));
    } else {
      this.prizes.push(data);
    }
    this.prizeFormShown = false;
  }

  async mounted(): Promise<void> {
    const { data } = await fetchPrizes();
    this.prizes = data || [];
    this.isFetching = false;
  }
}
